import React, { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import type { RebalanceType } from "../../../api/src/rebalances/rebalances.service";
import { formatCurrency } from "../lib/numbers";

/** An intraday cash adjustment entered on the Create Trade Proposal page. */
export type CashOverride = {
  /**
   * Signed dollars added to the reported cash: positive for cash that
   * arrived, negative for cash that left. Never `0`.
   */
  adjustment: number;
  reason: string;
};

/** `+$20,000` or `−$5,000`: a signed amount, its sign always shown. */
export function formatSignedCurrency(value: number, precision = 0) {
  return `${value < 0 ? "−" : "+"}${formatCurrency(Math.abs(value), precision)}`;
}

interface CashOverrideModalProps {
  show: boolean;
  accountName: string;
  /** The cash the custodian last reported. */
  reportedCashBalance: number;
  /** The adjustment already entered for the account, if any. */
  value: CashOverride | null;
  /**
   * The type of the trade proposal being created. A Tolerance proposal says
   * what it does with cash below $0; Raise Cash and Spend Cash say it on the
   * account row (`raiseSpendCashWarning`).
   */
  rebalanceType?: RebalanceType;
  onSave: (value: CashOverride) => void;
  onClear: () => void;
  onHide: () => void;
}

/** A signed dollar amount with at most two decimal places, such as `-5000.5`. */
const SIGNED_AMOUNT = /^[+-]?(\d+(\.\d{0,2})?|\.\d{1,2})$/;

/**
 * The "Adjust" form of an account's cash on the Create Trade Proposal page:
 * a signed amount to add to the cash the custodian last reported, and why.
 * The adjustment applies to the proposal being created only; the account's
 * cash is not changed. The resulting cash may be below $0, for a withdrawal
 * larger than the reported cash: the sum is then shown with its minus sign,
 * and, for a Tolerance proposal, a muted note that the rebalancer
 * (`python/src/rebalance/cash_balance_override.py`) sells to cover the
 * shortfall (R84).
 *
 * The form is rendered in a modal, whose portal is still inside the page's
 * React tree, so its submit is stopped here rather than reaching the page's
 * own form and creating the proposal.
 */
export default function CashOverrideModal({
  show,
  accountName,
  reportedCashBalance,
  value,
  rebalanceType,
  onSave,
  onClear,
  onHide,
}: CashOverrideModalProps) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (show) {
      setAmount(value === null ? "" : String(value.adjustment));
      setReason(value?.reason ?? "");
      setSubmitted(false);
    }
  }, [show, value]);

  // A minus sign typed as "−" (U+2212) reads the same as "-".
  const typed = amount.trim().replace(/^−/, "-").replace(/,/g, "");
  const parsed = SIGNED_AMOUNT.test(typed) ? Number(typed) : NaN;
  const amountError =
    typed === ""
      ? "Enter the cash adjustment."
      : !Number.isFinite(parsed)
        ? "Enter a dollar amount with at most two decimal places, such as 20000 or -5000."
        : parsed === 0
          ? "The adjustment must not be zero."
          : null;
  // In cents, so sub-cent noise in the reported cash does not read as below $0.
  const adjustedCash = Math.round((reportedCashBalance + parsed) * 100) / 100;
  const showsShortfallNote =
    amountError === null && adjustedCash < 0 && rebalanceType === "tolerance";
  const reasonError =
    reason.trim() === "" ? "Enter the reason for the adjustment." : null;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSubmitted(true);
    if (amountError !== null || reasonError !== null) return;
    onSave({ adjustment: parsed, reason: reason.trim() });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form noValidate onSubmit={handleSubmit} aria-label="Adjust cash">
        <Modal.Header closeButton>
          <Modal.Title as="h5">Adjust cash: {accountName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Add cash that arrived since the custodian's last report, or subtract
            cash that left, with a minus sign. The adjustment applies to this
            trade proposal only and does not change the account.
          </p>
          <Form.Group className="mb-3" controlId="cashOverrideAmount">
            <Form.Label>Cash adjustment</Form.Label>
            <InputGroup hasValidation>
              <InputGroup.Text>±$</InputGroup.Text>
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="e.g. 20000 or -5000"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                isInvalid={submitted && amountError !== null}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">
                {amountError}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          <div className="mb-3" data-testid="cash-adjustment-equation">
            <small className="text-muted d-block">
              Reported cash {formatCurrency(reportedCashBalance)}
              {amountError === null ? (
                <>
                  {" "}
                  {parsed < 0 ? "−" : "+"} adjustment{" "}
                  {formatCurrency(Math.abs(parsed))} ={" "}
                  <strong className={adjustedCash < 0 ? "text-danger" : ""}>
                    {adjustedCash < 0 ? "−" : ""}
                    {formatCurrency(Math.abs(adjustedCash))}
                  </strong>
                </>
              ) : null}
            </small>
            {showsShortfallNote && (
              <small
                className="text-muted d-block mt-1"
                data-testid="negative-cash-note"
              >
                The proposal will sell to cover the{" "}
                {formatCurrency(Math.abs(adjustedCash))} shortfall.
              </small>
            )}
          </div>
          <Form.Group controlId="cashOverrideReason">
            <Form.Label>Reason</Form.Label>
            <Form.Control
              type="text"
              maxLength={1000}
              placeholder="e.g. $20,000 deposit landed this morning"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              isInvalid={submitted && reasonError !== null}
            />
            <Form.Control.Feedback type="invalid">
              {reasonError}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          {value !== null && (
            <Button
              variant="outline-danger"
              className="me-auto"
              onClick={onClear}
            >
              Clear adjustment
            </Button>
          )}
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
